import React, { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import apiClient from "../api/client";
import { ActivityEvent } from "../types";
import ShowPersonalToggle from "../components/ShowPersonalToggle";
import ShowSystemToggle from "../components/ShowSystemToggle";
import useWorkspaceList from "../hooks/useWorkspaceList";
import DataTable from "../components/DataTable";
import DateRangePicker from "../components/DateRangePicker";
import ExportButton from "../components/ExportButton";
import ChangeSourceBadge from "../components/ChangeSourceBadge";
import ChangeCategoryBadge from "../components/ChangeCategoryBadge";
import ChangeDetailPanel from "../components/ChangeDetailPanel";
import DetailModal from "../components/DetailModal";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, utcDaysAgo, utcToday } from "../utils/datetime";

function today(): string {
  return utcToday();
}

// ---------------------------------------------------------------------------
// Activity Event Detail Modal
// ---------------------------------------------------------------------------

interface ActivityDetailModalProps {
  event: ActivityEvent | null;
  onClose: () => void;
}

function ActivityDetailModal({ event, onClose }: ActivityDetailModalProps) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  if (!event) return null;

  const keyFields: Array<{ label: string; value: string }> = [
    { label: "Date / Time", value: formatDate(event.timestampActivity) },
    { label: "Activity Type", value: event.activityType ?? "—" },
    { label: "Actor Name", value: event.actorName ?? "—" },
    { label: "Actor Email", value: event.actorEmail ?? "—" },
    { label: "Report", value: event.reportName ?? "—" },
    { label: "Workspace", value: event.workspaceName ?? "—" },
    { label: "Change Type", value: event.changeType ?? "—" },
    { label: "Commit SHA", value: event.commitSha ?? "—" },
  ];

  return (
    <DetailModal
      title={event.activityType ?? "Activity Event"}
      width="md"
      onClose={onClose}
      headerExtra={
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <ChangeSourceBadge changeSource={event.changeSource} />
          <ChangeCategoryBadge changeCategory={event.changeCategory} />
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {keyFields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
                <dd className="mt-0.5 text-sm text-gray-900 break-all">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Change Detail</h3>
          <ChangeDetailPanel event={event} />
        </div>
        {event.repoUrl && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Repo URL</dt>
            <dd className="mt-0.5">
              <a href={event.repoUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {event.repoUrl}
              </a>
            </dd>
          </div>
        )}
      </div>
    </DetailModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ActivityPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const { workspaces, loading: wsLoading, showPersonal, setShowPersonal } = useWorkspaceList({ monitoredOnly: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SOX review default: last 3 months to today
  const [dateFrom, setDateFrom] = useState(utcDaysAgo(90));
  const [dateTo, setDateTo] = useState(today());
  const [workspaceId, setWorkspaceId] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [changeSourceFilter, setChangeSourceFilter] = useState("");
  const [changeCategoryFilter, setChangeCategoryFilter] = useState("");
  const [showSystem, setShowSystem] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);

  const fetchActivity = () => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { limit: "1000" };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (workspaceId) params.workspace_id = workspaceId;
    if (actorEmail) params.actor_email = actorEmail;
    if (changeCategoryFilter) params.change_category = changeCategoryFilter;
    if (showSystem) params.show_system = "true";
    // The endpoint restricts to monitored workspaces by default, so widening
    // the dropdown alone was not enough: picking a personal workspace from it
    // returned an empty table, which reads as "nothing happened in there"
    // rather than "that workspace is outside the requested scope".
    if (showPersonal) params.monitored_only = "false";

    apiClient
      .get<ActivityEvent[]>("/api/activity", { params })
      .then((res) => setEvents(res.data))
      .catch((err) => setError(err.message ?? "Failed to load activity."))
      .finally(() => setLoading(false));
  };

  // Initial load, plus an immediate refetch when the system-activity view mode
  // changes (the composed filters wait for Apply).
  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSystem, showPersonal]);

  const columns: ColumnDef<ActivityEvent, string>[] = [
    { accessorKey: "timestampActivity", header: "Date / Time", cell: ({ getValue }) => formatDate(getValue()) },
    {
      id: "changeSource",
      header: "Source",
      accessorFn: (row) => row.changeSource ?? "",
      cell: ({ row }) => <ChangeSourceBadge changeSource={row.original.changeSource} />,
    },
    {
      id: "changeCategory",
      header: "Category",
      accessorFn: (row) => row.changeCategory ?? "",
      cell: ({ row }) => <ChangeCategoryBadge changeCategory={row.original.changeCategory} />,
    },
    { accessorKey: "reportName", header: "Report", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workspaceName", header: "Workspace", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "activityType", header: "Activity", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "actorName", header: "Actor", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "actorEmail", header: "Actor Email", cell: ({ getValue }) => getValue() || "—" },
    {
      id: "details",
      header: "",
      cell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); setSelectedEvent(row.original); }}
          className="text-xs text-blue-600 hover:underline"
        >
          Details
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Feed</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Power BI change activity in monitored workspaces. Manage scope under Advanced → Workspaces.
          </p>
        </div>
        <ExportButton
          endpoint="/api/export/changes"
          params={{
            date_from: dateFrom,
            date_to: dateTo,
            workspace_id: workspaceId || undefined,
            actor_email: actorEmail || undefined,
            show_system: showSystem ? "true" : undefined,
          }}
          filename="activity-export.csv"
        />
      </div>

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Workspace</label>
            <select
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              disabled={wsLoading}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-52 disabled:text-gray-400"
            >
              <option value="">{wsLoading ? "Loading workspaces…" : "All workspaces"}</option>
              {workspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>
                  {ws.name}
                </option>
              ))}
            </select>
          </div>

          <div className="pb-1.5">
            <ShowPersonalToggle show={showPersonal} onChange={setShowPersonal} />
          </div>

          <div className="pb-1.5">
            <ShowSystemToggle show={showSystem} onChange={setShowSystem} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Actor email</label>
            <input
              type="email"
              value={actorEmail}
              onChange={(e) => setActorEmail(e.target.value)}
              placeholder="user@example.com"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-52"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Source</label>
            <select
              value={changeSourceFilter}
              onChange={(e) => setChangeSourceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-44"
            >
              <option value="">All Sources</option>
              <option value="activity_event">Activity Log</option>
              <option value="git_commit">Git Commits</option>
              <option value="snapshot_diff">Detected Changes</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Category</label>
            <select
              value={changeCategoryFilter}
              onChange={(e) => setChangeCategoryFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-44"
            >
              <option value="">All Categories</option>
              <option value="report">Report</option>
              <option value="connection">Connection</option>
              <option value="schedule">Schedule</option>
            </select>
          </div>

          <button
            onClick={fetchActivity}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors self-end"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading activity…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && (() => {
        const filtered = changeSourceFilter
          ? events.filter((e) => (e.changeSource ?? "activity_event") === changeSourceFilter)
          : events;
        return (
          <>
            <p className="text-sm text-gray-500">
              {filtered.length} event{filtered.length !== 1 ? "s" : ""} returned
              {events.length >= 1000 && " (limit reached — narrow your date range for more precise results)"}.
            </p>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <DataTable data={filtered} columns={columns} initialPageSize={50} />
            </div>
          </>
        );
      })()}

      {/* Modal */}
      <ActivityDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
}
